import Delete from "@mui/icons-material/Delete";
import { Alert, Button } from "@mui/material";
import { GridRowParams } from "@mui/x-data-grid-premium";
import { Dialog } from "components/Dialog";
import { SkillAudience, SkillScope } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useTenant } from "hooks/useTenant";
import { useCallback, useEffect, useState } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";

import type { SkillRow } from "./skillsGridColumns";

import { useScoutSkillsLibraryEnabled } from "../useScoutSkillsLibraryEnabled";
import { DeleteSkillDialog } from "./DeleteSkillDialog";
import { SkillEditor, type SkillEditorFormValues } from "./SkillEditor";
import { canDeleteSkill, isSkillFormReadOnly } from "./skillPermissions";
import { skillReuseNote } from "./skillReuseNote";
import { SkillsListGrid } from "./SkillsListGrid";
import { SkillTitleBarControls } from "./SkillTitleBarControls";
import { SkillTitleInput } from "./SkillTitleInput";
import { UnsavedChangesDialog } from "./UnsavedChangesDialog";
import { useManageSkillsMutations } from "./useManageSkillsMutations";
import { type SkillFileTreeController, useSkillFileEditor } from "./useSkillFileEditor";
import { useUnsavedChangesPrompt } from "./useUnsavedChangesPrompt";

type DialogMode = "list" | "create" | "edit";

function emptySkillForm(): SkillEditorFormValues {
  return {
    displayName: "",
    description: "",
    scope: SkillScope.Tenant,
    audience: SkillAudience.Private,
    enabled: true,
  };
}

function skillFormValues(skill: SkillRow): SkillEditorFormValues {
  return {
    displayName: skill.displayName,
    description: skill.description,
    scope: skill.scope,
    audience: skill.audience,
    enabled: skill.enabled,
  };
}

type ManageSkillsDialogProps = {
  open: boolean;
  onClose: () => void;
};

/** Leaving (Back, Escape, backdrop) asks before dropping unsaved work; Save includes the open file. */
function useSkillDialogExits(params: {
  mode: DialogMode;
  readOnly: boolean;
  hasUnsavedWork: boolean;
  form: UseFormReturn<SkillEditorFormValues>;
  fileEditor: SkillFileTreeController;
  editingSkill: SkillRow | null;
  saveSkill: ReturnType<typeof useManageSkillsMutations>["saveSkill"];
  onClose: () => void;
  resetToList: () => void;
}) {
  const { mode, readOnly, hasUnsavedWork, form, fileEditor, editingSkill, saveSkill } = params;
  const { onClose, resetToList } = params;
  const { ask, dialogProps } = useUnsavedChangesPrompt();

  const handleCloseOrBack = useCallback(async () => {
    if (mode === "list") {
      onClose();
      return;
    }
    if (hasUnsavedWork && !readOnly) {
      const choice = await ask({
        canSave: false,
        message:
          mode === "create"
            ? "This skill hasn't been created yet. Leaving discards it."
            : "This skill has unsaved changes. Leaving discards them.",
      });
      if (choice !== "discard") {
        return;
      }
    }
    resetToList();
  }, [ask, hasUnsavedWork, mode, onClose, readOnly, resetToList]);

  const onSave = form.handleSubmit(async (values) => {
    if (readOnly || mode === "list") {
      return;
    }
    if (mode === "create") {
      await saveSkill(mode, values, editingSkill, fileEditor.draftFiles ?? {});
      return;
    }
    if (fileEditor.dirty && !(await fileEditor.handleSave())) {
      return;
    }
    if (form.formState.isDirty) {
      await saveSkill(mode, values, editingSkill, {});
    } else {
      resetToList();
    }
  });

  return { handleCloseOrBack, onSave, discardDialogProps: dialogProps };
}

export function ManageSkillsDialog({ open, onClose }: ManageSkillsDialogProps) {
  const { tenantId } = useTenant();
  const { user, isStaff } = useCurrentUser();
  const skillsLibraryEnabled = useScoutSkillsLibraryEnabled();

  const [mode, setMode] = useState<DialogMode>("list");
  const [editingSkill, setEditingSkill] = useState<SkillRow | null>(null);
  const [skillToDelete, setSkillToDelete] = useState<SkillRow | null>(null);

  const form = useForm<SkillEditorFormValues>({
    defaultValues: emptySkillForm(),
    mode: "onChange",
  });

  const {
    control,
    reset,
    formState: { isDirty, isValid },
  } = form;

  const resetToList = useCallback(() => {
    setMode("list");
    setEditingSkill(null);
    reset(emptySkillForm());
  }, [reset]);

  const handleDeleted = useCallback(
    (skillId: string) => {
      setSkillToDelete(null);
      if (mode === "edit" && editingSkill?.id === skillId) {
        resetToList();
      }
    },
    [editingSkill?.id, mode, resetToList]
  );

  const handleSaved = useCallback(
    (skill?: SkillRow) => {
      if (skill) {
        setEditingSkill(skill);
        reset(skillFormValues(skill));
        setMode("edit");
        return;
      }
      resetToList();
    },
    [reset, resetToList]
  );

  const { saveSkill, deleteSkill, saving, deleting } = useManageSkillsMutations({
    tenantId,
    isStaff,
    onDeleted: handleDeleted,
    onSaved: handleSaved,
  });

  useEffect(() => {
    if (!open) {
      resetToList();
      setSkillToDelete(null);
    }
  }, [open, resetToList]);

  const readOnly = isSkillFormReadOnly(mode, editingSkill, user?.id, isStaff);
  const editingSkillId = mode === "edit" ? (editingSkill?.id ?? null) : null;
  const fileEditor = useSkillFileEditor({
    active: mode !== "list",
    skillId: editingSkillId,
    tenantId,
    readOnly,
  });
  const hasUnsavedWork = isDirty || fileEditor.hasUnsavedWork;
  const { handleCloseOrBack, onSave, discardDialogProps } = useSkillDialogExits({
    mode,
    readOnly,
    hasUnsavedWork,
    form,
    fileEditor,
    editingSkill,
    saveSkill,
    onClose,
    resetToList,
  });

  const deletableSkill =
    mode === "edit" && editingSkill && canDeleteSkill(editingSkill, user?.id, isStaff)
      ? editingSkill
      : null;

  const canDeleteRow = useCallback(
    (skill: SkillRow) => canDeleteSkill(skill, user?.id, isStaff),
    [isStaff, user?.id]
  );

  const handleAddSkill = useCallback(() => {
    setEditingSkill(null);
    reset(emptySkillForm());
    setMode("create");
  }, [reset]);

  const handleRowClick = useCallback(
    (params: GridRowParams<SkillRow>) => {
      const skill = params.row;
      setEditingSkill(skill);
      reset(skillFormValues(skill));
      setMode("edit");
    },
    [reset]
  );

  if (!skillsLibraryEnabled) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={() => void handleCloseOrBack()}
        title={
          mode === "list" ? (
            "Manage Skills"
          ) : (
            <SkillTitleInput
              key={editingSkill?.id ?? "new"}
              control={control}
              readOnly={readOnly}
              autoFocus={mode === "create"}
            />
          )
        }
        fullWidth
        maxWidth="lg"
        noPrimaryAction={mode === "list" || readOnly}
        additionalPrimaryActionElements={
          mode === "list"
            ? [
                <Button key="close" onClick={onClose}>
                  Close
                </Button>,
              ]
            : undefined
        }
        cancelButtonProps={{ text: "Back" }}
        primaryActionButtonProps={{
          onClick: onSave,
          loading: saving,
          disabled: !hasUnsavedWork || !isValid || !tenantId,
        }}
        secondaryActionElements={
          deletableSkill
            ? [
                <Button
                  key="delete-skill"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setSkillToDelete(deletableSkill)}
                  disabled={deleting}
                >
                  Delete
                </Button>,
              ]
            : undefined
        }
        titleBarActionElement={
          mode === "list" ? undefined : (
            <SkillTitleBarControls
              control={control}
              readOnly={readOnly}
              isStaff={isStaff}
              showEnabled={mode === "edit"}
            />
          )
        }
      >
        {mode === "list" ? (
          <SkillsListGrid
            tenantId={tenantId}
            open={open}
            onAddSkill={handleAddSkill}
            onRowClick={handleRowClick}
            onDeleteSkill={setSkillToDelete}
            canDeleteSkill={canDeleteRow}
            deleting={deleting}
          />
        ) : (
          <>
            {readOnly ? null : (
              <Alert severity="info" sx={{ mb: 2 }}>
                {skillReuseNote(isStaff)}
              </Alert>
            )}
            <SkillEditor
              control={control}
              readOnly={readOnly}
              fileEditor={fileEditor}
              fileTreeKey={editingSkillId ?? "draft"}
            />
          </>
        )}
      </Dialog>

      <DeleteSkillDialog
        skill={skillToDelete}
        onClose={() => setSkillToDelete(null)}
        onConfirm={deleteSkill}
        deleting={deleting}
      />
      <UnsavedChangesDialog {...discardDialogProps} />
    </>
  );
}
